'use client'

import { motion, useAnimation, useWillChange } from 'motion/react'
import { ReactNode, useId } from 'react'
import { useMeasure } from 'react-use'
import { IOption } from '@/typings/option'
import { cn } from '@/utils/styles/cn'

type AnimationControls = ReturnType<typeof useAnimation>

export type IModuleSwitchOption<V, L extends string | ReactNode = string> = IOption<V, L> & {
    animationControls?: AnimationControls
}

interface ModuleSwitchProps<V, L extends string | ReactNode = string> {
    selectedOptionValue: V | undefined | null
    options: IModuleSwitchOption<V, L>[]
    setSelectedOption: (value: V) => void
    className?: string
    makeItemRef?: (value: V, isLast: boolean) => (element: HTMLDivElement) => void
}

export function ModuleSwitch<T, L extends string | ReactNode = string>({
    options,
    selectedOptionValue,
    setSelectedOption,
    className,
    makeItemRef,
}: ModuleSwitchProps<T, L>) {
    const componentId = useId()

    return (
        <div
            className={cn(
                'flex items-center relative z-[1] gap-x-6 md:gap-x-2 border-b-[1px] border-gray-300 min-w-fit',
                className
            )}
        >
            {options.map(({ label, value, animationControls }, index, arr) => {
                const isLast = index === arr.length - 1
                const isSelected = value === selectedOptionValue

                return (
                    <div
                        ref={makeItemRef?.(value, isLast)}
                        key={'module-switch' + index + componentId}
                    >
                        <ModuleSwitchItem
                            animationControls={animationControls}
                            label={label}
                            value={value}
                            isSelected={isSelected}
                            setSelectedOption={setSelectedOption}
                            componentId={componentId}
                        />
                    </div>
                )
            })}
        </div>
    )
}

interface IModuleSwitchItemProps<V, L extends string | ReactNode = string> {
    label: L
    value: V
    animationControls?: AnimationControls
    isSelected: boolean
    setSelectedOption: (value: V) => void
    componentId: string
}

const ModuleSwitchItem = <T, L extends string | ReactNode = string>({
    label,
    value,
    isSelected,
    setSelectedOption,
    componentId,
    animationControls,
}: IModuleSwitchItemProps<T, L>) => {
    const willChange = useWillChange()

    const [contentRef, { width: contentWidth }] = useMeasure<HTMLDivElement>()

    return (
        <motion.div animate={animationControls} style={{ borderRadius: 'inherit' }}>
            <motion.div
                initial={{ width: 'auto' }}
                animate={{ width: contentWidth || 'auto' }}
                transition={{ width: { type: 'spring', stiffness: 300, damping: 22 } }}
                onClick={() => setSelectedOption(value)}
                style={{
                    willChange,
                    cursor: isSelected ? 'default' : 'pointer',
                    borderRadius: 'inherit',
                }}
                className="flex items-center justify-center h-[52px] z-[0] relative select-none"
            >
                <div ref={contentRef} className="flex items-center justify-center">
                    {isSelected && (
                        <motion.span
                            layoutId={'module-switch-highlighter' + componentId}
                            transition={{
                                layout: { type: 'spring', damping: 44, stiffness: 600 },
                            }}
                            className="h-[52px] bg-transparent w-full absolute left-0 z-[-1] border-b-[3px] border-black"
                        />
                    )}

                    <div className="py-0 md:px-4 px-0">
                        <motion.span
                            initial={{ opacity: isSelected ? 1 : 0.65 }}
                            animate={{ opacity: isSelected ? 1 : 0.65 }}
                            className="font-bold whitespace-nowrap z-[1] relative text-black"
                            style={{ transition: 'color 0.12s ease-out' }}
                        >
                            {label}
                        </motion.span>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    )
}
